import { supabaseClient } from '../../utils/supabase';
import type { DealPrepConfig } from './dealPrepConfigShared';

export {
  DEAL_PREP_BRANDS,
  DEAL_PREP_UPLOAD_BRANDS,
  isEmail,
  parseEmailList,
  type DealPrepConfig,
} from './dealPrepConfigShared';

// The deal prep RPCs and global_setting.deal_prep_config postdate the
// generated database types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabaseClient as any;

/** One flag line on a DEAL PREP message (REG-858). */
export interface DealPrepFlag {
  level: 'red' | 'amber' | 'blue';
  text: string;
}

export interface DealPrepMessageView {
  id: number;
  kind: 'dealprep' | 'append' | 'alert' | 'request' | 'reminder' | 'history';
  status: 'queued' | 'sent' | 'failed' | 'imported';
  sandbox: boolean;
  created_at: string;
  sent_at: string | null;
  from_email: string;
  to: string[];
  cc: string[];
  intended_to: string[];
  intended_cc: string[];
  flags: DealPrepFlag[];
  attachments: Array<{ filename: string; role?: string; size_bytes?: number; drive_file_id?: string }>;
  info_only: boolean;
  error: string | null;
  gmail_url: string | null;
}

export interface DealPrepChainView {
  id: number;
  subject: string;
  brand: string;
  verdict: 'new' | 'shopping' | 'previous' | 'mismatch';
  source: string;
  rep: string | null;
  sender_email: string;
  to: string[];
  cc: string[];
  created_at: string;
  last_message_at: string | null;
  drive_folder_url: string | null;
  deal_cycle: number | null;
  messages: DealPrepMessageView[];
  applications: Array<{ form: number; provider: string | null; signed_at: string | null; name: string | null }>;
}

export interface DealPrepIntakeView {
  job: number;
  form: number;
  status: 'queued' | 'processing' | 'done' | 'skipped' | 'failed';
  outcome: string | null;
  verdict: string | null;
  matched_by: string | null;
  error: string | null;
  attempts: number;
  created_at: string;
  updated_at: string;
  deal_prep: number | null;
}

export interface MerchantLinkCheckView {
  created_at: string;
  ok: boolean;
  verify: boolean;
  matched_by: 'phone' | 'email';
  application_business: string | null;
  reason: string | null;
}

export interface MerchantDealPrepView {
  chains: DealPrepChainView[];
  intake: DealPrepIntakeView[];
  link_checks: MerchantLinkCheckView[];
  drive_folder_url: string | null;
}

export const dealPrepKeys = {
  merchant: (merchantId: number) => ['deal-prep', 'merchant', merchantId] as const,
  config: ['deal-prep', 'config'] as const,
};

/** public.deal_prep_merchant_view: runs as the user, so RLS decides what comes back. */
export async function fetchMerchantDealPrep(merchantId: number): Promise<MerchantDealPrepView> {
  const { data, error } = await db.rpc('deal_prep_merchant_view', { p_merchant: merchantId });
  if (error) throw error;
  return (data as MerchantDealPrepView | null) ?? { chains: [], intake: [], link_checks: [], drive_folder_url: null };
}

export async function fetchDealPrepConfig(): Promise<{ id: number; config: DealPrepConfig }> {
  const { data, error } = await db
    .schema('core_crm')
    .from('global_setting')
    .select('id,deal_prep_config')
    .order('id')
    .limit(1)
    .single();
  if (error) throw error;
  return { id: data.id as number, config: data.deal_prep_config as DealPrepConfig };
}


/** One intake job from public.deal_prep_intake_list (REG-859 part 2). */
export interface DealPrepIntakeJob {
  job: number;
  form: number;
  status: 'queued' | 'processing' | 'done' | 'skipped' | 'failed';
  outcome: string | null;
  error: string | null;
  attempts: number;
  brand: string | null;
  source: string | null;
  created_at: string;
  updated_at: string;
  next_attempt_at: string;
  deal_prep: number | null;
  business_name: string | null;
  merchant: number | null;
  signer_email: string | null;
  form_error: string | null;
}

export const dealPrepIntakeKey = (statuses: string[]) => ['deal-prep', 'intake', ...statuses] as const;

export async function fetchDealPrepIntake(statuses: string[]): Promise<DealPrepIntakeJob[]> {
  const { data, error } = await db.rpc('deal_prep_intake_list', { p_statuses: statuses, p_limit: 300 });
  if (error) throw error;
  return (data as DealPrepIntakeJob[] | null) ?? [];
}

/** Puts a failed or skipped job back in the queue now. */
export async function retryDealPrepIntake(job: number): Promise<void> {
  const { error } = await db.rpc('deal_prep_intake_retry', { p_job: job });
  if (error) throw error;
}

/* ── REG-859: Deal Prep tab actions and the failures list ─────────────── */

export type DealPrepChainAction = 'resend' | 'append_latest';

export interface DealPrepActionFile {
  role: 'application' | 'statement';
  filename: string;
  size_bytes: number;
  sent_at?: string | null;
}

/** deal-prep-actions response for documents / resend / append_latest. */
export interface DealPrepActionResult {
  ok: true;
  chain: number;
  files: DealPrepActionFile[];
  skipped: DealPrepActionFile[];
  bytes: number;
  has_application: boolean;
  unscrubbed_only: boolean;
  card_stored: boolean;
  duplicate?: boolean;
  message_id?: number;
  sandbox?: boolean;
}

async function invokeDealPrepActions<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabaseClient.functions.invoke('deal-prep-actions', { body });
  if (error) {
    // supabase-js wraps non-2xx responses; surface the server's message.
    const context = (error as { context?: Response }).context;
    const payload = context ? await context.json().catch(() => null) : null;
    throw new Error(payload?.error ? String(payload.error) : error.message ?? 'The deal prep service rejected the request');
  }
  if (!data?.ok) throw new Error(data?.error ?? 'Empty response from the deal prep service');
  return data as T;
}

/** What Resend / Append would attach; nothing is sent. */
export function fetchChainDocuments(merchantId: number, chainId: number): Promise<DealPrepActionResult> {
  return invokeDealPrepActions({ action: 'documents', merchantId, chainId });
}

/** Queues the message; requestId (one per dialog) makes a double click send once. */
export function runChainAction(action: DealPrepChainAction, merchantId: number, chainId: number, requestId: string): Promise<DealPrepActionResult> {
  return invokeDealPrepActions({ action, merchantId, chainId, requestId });
}

/** REG-734: finds or creates the merchant's Drive folder and stores the link. */
export function createMerchantDriveFolder(merchantId: number): Promise<{ ok: true; url: string; how: string }> {
  return invokeDealPrepActions({ action: 'drive_folder', merchantId });
}

export interface DealPrepFailure {
  kind: 'drive_share' | 'send' | 'upload' | 'scrub' | 'no_match';
  id: number | string;
  at: string;
  merchant: number | null;
  business_name: string | null;
  chain: number | null;
  reason: string;
  retry: boolean;
}

export const dealPrepFailuresKey = ['deal-prep', 'failures'] as const;

export async function fetchDealPrepFailures(): Promise<DealPrepFailure[]> {
  const { data, error } = await db.rpc('deal_prep_failures_list', { p_limit: 300 });
  if (error) throw error;
  return (data as DealPrepFailure[] | null) ?? [];
}

/** Puts a failed Drive share back in the queue. */
export async function retryDealPrepDriveShare(job: number): Promise<void> {
  const { error } = await db.rpc('deal_prep_drive_share_retry', { p_job: job });
  if (error) throw error;
}
